import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Events, Platform } from 'ionic-angular';
import { APP_CONFIG, Env } from '../../environment';
import { HttpProvider } from '../../providers/http/http';
import { AuthProvider } from '../../providers/authorization/auth';
import { StorageProvider } from '../../providers/storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { CustomerProvider } from '../../providers/customer/customer';
import { ResourceProvider } from '../../providers/resource/resource';
import { DebugErrorProvider, DebugErrorFlow, DebugErrorOption } from '../../providers/debug-error/debug-error';
import { RouteProvider } from '../../providers/route/route';
import { SystemProvider } from '../../providers/system/system';
import { FeatureFlagTypes, UtilsProvider } from '../../providers/myria/utils-provider';
import { EVENTS_NAME } from '../../providers/myria/app-variables';
import {
  LANDING_PAGE_AB_TEST_VARIANTS,
  RMT5_DEBUG_FLAGS,
  RMT5_FEATURE_FLAGS,
  RMT5_FLAG_MODES,
  RMT5_MIGRATION_FLAGS,
  Rmt5FlagMode,
  rmt5ForcedValue
} from '../../model/types/feature-flag-types';
import { RmtPopupComponent } from '../rmt-popup/rmt-popup';
import { XeCaptchaComponent } from '../xe-captcha/xe-captcha';
import { brandings } from '../../environment/branding/brandings';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import {
  FOREIGN_IP_TEST_LOCATIONS,
  FOREIGN_IP_TEST_OVERRIDE_DEFAULTS,
  FOREIGN_IP_TEST_TIME_ZONES,
  ForeignIpGeolocationTestOverrides,
  IForeignIpTestOverrides
} from '../../providers/foreign-ip-geolocation/foreign-ip-geolocation-test-overrides';
import { DeviceProvider } from '../../providers/device/device';

export interface IRmt5FlagRow {
  flag: FeatureFlagTypes;
  overridden: boolean;
  value: boolean;
  serverValue: boolean;
  hint?: string;
}

export interface IRmt5FlagSection {
  title: string;
  rows: IRmt5FlagRow[];
}

export interface IDebugMenuDoneResult {
  rmt5OverridesChanged: boolean;
  settingsChanged: boolean;
}

const AUTOMATION_RECAPTCHA_KEY = 'c1ef6349-ff35-4669-a583-cf4491375d50';
const AB_TEST_OFF = 'off';
const HOME_DEBUG_FLAGS: FeatureFlagTypes[] = [FeatureFlagTypes.HomeRevamp];
const RMT5_FLAG_MODE_LABELS: { [mode in Rmt5FlagMode]: string } = { legacy: 'Legacy', off: 'Off', full: 'Full' };

@Component({
  selector: 'rmt-debug-menu',
  templateUrl: 'rmt-debug-menu.html',
})
export class RmtDebugMenuComponent implements OnInit {
  @Output() done = new EventEmitter<IDebugMenuDoneResult>();
  @Output() brandingChanged = new EventEmitter<void>();

  environment = APP_CONFIG.env;
  environments = [
    { value: Env.DEV, text: 'DEV' },
    { value: Env.QA, text: 'QA' },
    { value: Env.STG, text: 'Staging' },
    { value: Env.PROD, text: 'Production' },
  ];
  api = APP_CONFIG.API_URL;
  apis = APP_CONFIG.apis ? APP_CONFIG.apis : [];
  branding = APP_CONFIG.branding;
  brandings = [];
  apiVersion = HttpProvider.betaVersion;
  apiVersions = ['5.0.0', '5.0.1'];
  reminderDuration = APP_CONFIG.reminderInterval ? APP_CONFIG.reminderInterval : 'Monthly';
  reminderDurations = ['Minute', 'Hourly', 'Daily', 'Weekly', 'Monthly'];
  automationTest: boolean = APP_CONFIG.automationTest;
  amplitudeHeader: boolean = APP_CONFIG.amplitudeHeader;
  liteRegisterationMY: boolean = APP_CONFIG.liteRegisterationMY;
  showDebug: boolean = APP_CONFIG.showDebug;
  enforceRefresh: boolean = APP_CONFIG.enforceRefresh;
  showTransferStatusWidget: boolean = APP_CONFIG.showTransferStatusWidget;
  resourcesCleanCache: boolean = ResourceProvider.cleanCache;
  internalIssueReporter: boolean = false;
  forcedCompleteProfile: number | string = null;
  forcedLiteReg: number | string = null;
  forcedLogin: number | string = null;
  completeProfileCodes: DebugErrorOption[] = [];
  liteRegCodes: DebugErrorOption[] = [];
  loginCodes: DebugErrorOption[] = [];
  debugFlows = DebugErrorFlow;
  appInfo = 'unknown';

  showRmt5Flags = false;
  showForceErrors = false;
  showForeignIpTest = false;
  foreignIpOverrides: IForeignIpTestOverrides = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS };
  foreignIpTimeZones = FOREIGN_IP_TEST_TIME_ZONES;
  foreignIpLocations = FOREIGN_IP_TEST_LOCATIONS;
  foreignIpLocationSelection = '';
  deviceTimeZone = '';
  foreignIpLatitudeText = '';
  foreignIpLongitudeText = '';
  isAndroid = this.platform.is('android');
  private foreignIpSnapshot = JSON.stringify({ ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS });
  private foreignIpInitialised = false;
  rmt5FlagSections: IRmt5FlagSection[] = [];
  rmt5FlagRows: IRmt5FlagRow[] = [];
  homeFlagRows: IRmt5FlagRow[] = [];
  abVariants = LANDING_PAGE_AB_TEST_VARIANTS;
  abVariantSelection = '';
  rmt5FlagMode: Rmt5FlagMode = 'off';
  rmt5FlagModes = RMT5_FLAG_MODES;
  private rmt5FlagModeSnapshot: Rmt5FlagMode = 'off';
  private overridesSnapshot = '';
  private envSnapshot = '';
  private apiSnapshot = '';
  private doneEmitted = false;

  constructor(
    private platform: Platform,
    private httpProvider: HttpProvider,
    private authProvider: AuthProvider,
    private storageProvider: StorageProvider,
    private customerProvider: CustomerProvider,
    private resourceProvider: ResourceProvider,
    private routeProvider: RouteProvider,
    private systemProvider: SystemProvider,
    private utilsProvider: UtilsProvider,
    private debugError: DebugErrorProvider,
    private events: Events,
  ) {}

  static applyAutomationTestRecaptcha() {
    APP_CONFIG.recaptchaKey = AUTOMATION_RECAPTCHA_KEY;
    XeCaptchaComponent.updateSiteKey(APP_CONFIG.recaptchaKey);
  }

  async ngOnInit() {
    for (const branding in brandings) {
      this.brandings.push({ value: brandings[branding], text: branding });
    }
    this.initRmt5FlagRows();
    this.internalIssueReporter = UtilsProvider.featureFlagOverrides[FeatureFlagTypes.InternalIssueReporterEnabled] === true;
    this.rmt5FlagMode = UtilsProvider.rmt5FlagMode;
    this.rmt5FlagModeSnapshot = UtilsProvider.rmt5FlagMode;
    this.overridesSnapshot = this.canonicalOverrides(UtilsProvider.featureFlagOverrides);
    this.envSnapshot = APP_CONFIG.env;
    this.apiSnapshot = APP_CONFIG.API_URL;
    this.appInfo = await this.systemProvider.getAppReleaseInfo();
    this.forcedCompleteProfile = this.debugError.getRawSetting(DebugErrorFlow.CompleteProfile);
    this.forcedLiteReg = this.debugError.getRawSetting(DebugErrorFlow.LiteRegistration);
    this.forcedLogin = this.debugError.getRawSetting(DebugErrorFlow.Login);
    this.completeProfileCodes = this.debugError.getCodesForFlow(DebugErrorFlow.CompleteProfile);
    this.liteRegCodes = this.debugError.getCodesForFlow(DebugErrorFlow.LiteRegistration);
    this.loginCodes = this.debugError.getCodesForFlow(DebugErrorFlow.Login);
    await ForeignIpGeolocationTestOverrides.ready;
    this.initForeignIpTest();
  }

  get showSubPage(): boolean {
    return this.showRmt5Flags || this.showForceErrors || this.showForeignIpTest;
  }

  get modalTitle(): string {
    if (this.showRmt5Flags) {
      return 'RMT5 Flags';
    }
    if (this.showForceErrors) {
      return 'Force Error';
    }
    if (this.showForeignIpTest) {
      return 'Foreign IP test';
    }
    return 'Environment Config';
  }

  changeEnvironment(environment) {
    APP_CONFIG.env = environment;
  }

  changeAPI(apiCst) {
    for (let i = 0; i < this.apis.length; i++) {
      const api = this.apis[i];
      if (api.cst === apiCst) {
        this.httpProvider.setSessionEnv(api, this.authProvider, this.utilsProvider);
        return;
      }
    }
    RmtPopupComponent.showError('Failed to change API');
  }

  changeBranding(branding) {
    APP_CONFIG.branding = branding;
    this.branding = branding;
    this.resourceProvider.initializeResources(true, true);
    document.getElementsByTagName('body')[0].className = branding.css;
    this.customerProvider.setLanguage(HttpProvider.currentLanguage, false, false, true);
    this.brandingChanged.emit();
  }

  changeApiVersion(apiVersion: string) {
    HttpProvider.betaVersion = apiVersion;
    HttpProvider.version = apiVersion;
    HttpProvider.minVersion = apiVersion;
    this.apiVersion = apiVersion;
  }

  changeReminderDurations(reminderDuration) {
    APP_CONFIG.reminderInterval = reminderDuration;
    this.storageProvider.remove(StorageType.ReminderBiometric);
    this.reminderDuration = reminderDuration;
  }

  toggleAutomationTest() {
    APP_CONFIG.automationTest = this.automationTest;
    if (APP_CONFIG.automationTest) {
      RmtDebugMenuComponent.applyAutomationTestRecaptcha();
    }
  }

  toggleAmplitudeHeader() {
    APP_CONFIG.amplitudeHeader = this.amplitudeHeader;
  }

  toggleLiteRegisterationMY() {
    APP_CONFIG.liteRegisterationMY = this.liteRegisterationMY;
  }

  toggleDebug() {
    APP_CONFIG.showDebug = this.showDebug;
    window.requestAnimationFrame(() => {
      this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
    });
  }

  toggleRefresh() {
    APP_CONFIG.enforceRefresh = this.enforceRefresh;
  }

  toggleResourcesCleanCache() {
    ResourceProvider.cleanCache = this.resourcesCleanCache;
    this.resourceProvider.initializeResources(true, true);
  }

  changeForcedError(flow, value) {
    this.debugError.setForcedError(flow, value);
  }

  toggleTransferStatusWidget() {
    APP_CONFIG.showTransferStatusWidget = this.showTransferStatusWidget;
    window.requestAnimationFrame(() => {
      this.routeProvider.setRoot(this.routeProvider.pages.PreLoginPage);
    });
  }

  onOverrideToggled(row: IRmt5FlagRow) {
    if (row.overridden) {
      row.value = row.serverValue;
    }
  }

  openRmt5Flags() {
    this.showRmt5Flags = true;
  }

  // The Legacy and Full RMT5 modes lock the kill switch and the migration flags to the forced value.
  isRmt5FlagForced(row: IRmt5FlagRow): boolean {
    return rmt5ForcedValue(row.flag, this.rmt5FlagMode) !== null;
  }

  rmt5FlagShownValue(row: IRmt5FlagRow): boolean {
    const forced = rmt5ForcedValue(row.flag, this.rmt5FlagMode);
    if (forced !== null) {
      return forced;
    }
    return row.overridden ? row.value : row.serverValue;
  }

  rmt5FlagSourceText(row: IRmt5FlagRow): string {
    const state = this.rmt5FlagShownValue(row) ? 'ON' : 'OFF';
    if (this.isRmt5FlagForced(row)) {
      return `Forced by ${RMT5_FLAG_MODE_LABELS[this.rmt5FlagMode]}: ${state}`;
    }
    return `${row.overridden ? 'Override' : 'Server'}: ${state}`;
  }

  openForceErrors() {
    this.showForceErrors = true;
  }

  openForeignIpTest() {
    this.showForeignIpTest = true;
  }

  backToMain() {
    this.showRmt5Flags = false;
    this.showForceErrors = false;
    this.showForeignIpTest = false;
  }

  private initForeignIpTest() {
    this.foreignIpOverrides = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS, ...ForeignIpGeolocationTestOverrides.current };
    this.foreignIpSnapshot = JSON.stringify(this.foreignIpOverrides);
    this.syncForeignIpCoordinateText();
    this.foreignIpLocationSelection = this.describeLocationSelection();
    this.deviceTimeZone = DeviceProvider.getIntlTimeZone() || 'unknown';
    this.foreignIpInitialised = true;
  }

  private describeLocationSelection(): string {
    if (this.foreignIpOverrides.fixMode !== 'custom') {
      return this.foreignIpOverrides.fixMode;
    }
    const match = FOREIGN_IP_TEST_LOCATIONS.find((l) => l.latitude === this.foreignIpOverrides.latitude && l.longitude === this.foreignIpOverrides.longitude);
    return match ? match.text : 'custom';
  }

  changeForeignIpLocation(selection: string) {
    this.foreignIpLocationSelection = selection;
    if (selection === 'device' || selection === 'none') {
      this.foreignIpOverrides.fixMode = selection;
      return;
    }
    this.foreignIpOverrides.fixMode = 'custom';
    const preset = FOREIGN_IP_TEST_LOCATIONS.find((l) => l.text === selection);
    if (preset) {
      this.foreignIpOverrides.latitude = preset.latitude;
      this.foreignIpOverrides.longitude = preset.longitude;
      this.syncForeignIpCoordinateText();
    }
  }

  changeForeignIpCoordinate(field: 'latitude' | 'longitude', value: any) {
    let text = '';
    if (value !== null && value !== undefined) {
      text = String(value).trim().replace(',', '.');
    }
    if (field === 'latitude') {
      this.foreignIpLatitudeText = text;
    } else {
      this.foreignIpLongitudeText = text;
    }
    const parsed = parseFloat(text);
    this.foreignIpOverrides[field] = isNaN(parsed) ? null : parsed;
    this.foreignIpOverrides.fixMode = 'custom';
    this.foreignIpLocationSelection = 'custom';
  }

  private syncForeignIpCoordinateText() {
    this.foreignIpLatitudeText = RmtDebugMenuComponent.coordinateText(this.foreignIpOverrides.latitude);
    this.foreignIpLongitudeText = RmtDebugMenuComponent.coordinateText(this.foreignIpOverrides.longitude);
  }

  private static coordinateText(value: number | null): string {
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  async resetForeignIpTest() {
    this.foreignIpOverrides = { ...FOREIGN_IP_TEST_OVERRIDE_DEFAULTS };
    this.foreignIpLocationSelection = 'device';
    this.syncForeignIpCoordinateText();
    try {
      await ForeignIpGeolocationTestOverrides.reset(this.storageProvider);
      this.foreignIpSnapshot = JSON.stringify(this.foreignIpOverrides);
      this.foreignIpInitialised = true;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to clear foreign IP test overrides');
    }
  }

  private async saveForeignIpTest() {
    const unchanged = JSON.stringify(this.foreignIpOverrides) === this.foreignIpSnapshot;
    if (!this.foreignIpInitialised || unchanged) {
      return;
    }
    try {
      await ForeignIpGeolocationTestOverrides.save(this.storageProvider, this.foreignIpOverrides);
      this.foreignIpSnapshot = JSON.stringify(this.foreignIpOverrides);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to save foreign IP test overrides');
    }
  }

  async simulateStorageLoss() {
    try {
      await this.storageProvider.clearFirstRunIndicator();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to clear the first run indicator');
    }
    window.location.reload();
  }

  async onDone() {
    LoggerProvider.info(this, 'DebugMenu: onDone entry', `alreadyEmitted=${this.doneEmitted}`);
    if (this.doneEmitted) {
      return;
    }
    this.doneEmitted = true;

    if (this.environment && APP_CONFIG.env !== this.environment) {
      APP_CONFIG.env = this.environment;
    }
    if (this.api && APP_CONFIG.API_URL !== this.api) {
      this.changeAPI(this.api);
    }

    await this.saveForeignIpTest();

    const newOverrides = this.buildOverridesMap();
    const overridesChanged = this.canonicalOverrides(newOverrides) !== this.overridesSnapshot;
    const modeChanged = this.rmt5FlagMode !== this.rmt5FlagModeSnapshot;
    const changed = overridesChanged || modeChanged;
    LoggerProvider.info(this, 'DebugMenu: onDone computed changed', `changed=${changed}`);
    if (changed) {
      UtilsProvider.featureFlagOverrides = newOverrides;
      UtilsProvider.rmt5FlagMode = this.rmt5FlagMode;
      try {
        await UtilsProvider.saveFeatureFlagOverrides(this.storageProvider);
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to save feature flag overrides');
      }
      this.events.publish(EVENTS_NAME.FEATURE_FLAGS_UPDATED);
    }

    // RMT5 mode change: persist to localStorage (reliable on device, read at
    // startup with top precedence — no secure-storage dependency) and hard-reload
    // so the app re-boots cleanly into the selected mode instead of the fragile
    // in-place bridge transition. This makes legacy/off/full switch reliably.
    if (modeChanged) {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('rmt5Mode', this.rmt5FlagMode);
        }
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to persist rmt5Mode to localStorage');
      }
      window.location.reload();
      return;
    }

    const settingsChanged = APP_CONFIG.env !== this.envSnapshot || APP_CONFIG.API_URL !== this.apiSnapshot;
    LoggerProvider.info(this, 'DebugMenu: onDone before emit', `changed=${changed} settingsChanged=${settingsChanged}`);
    this.done.emit({ rmt5OverridesChanged: changed, settingsChanged });
  }

  private initRmt5FlagRows() {
    const toRow = (flag: FeatureFlagTypes): IRmt5FlagRow => {
      const overridden = UtilsProvider.featureFlagOverrides.hasOwnProperty(flag);
      const serverValue = this.serverFlagValue(flag);
      return {
        flag,
        overridden,
        value: overridden ? UtilsProvider.featureFlagOverrides[flag] === true : serverValue,
        serverValue,
      };
    };
    this.rmt5FlagSections = [
      { title: 'Bridge kill switch', rows: [{ ...toRow(FeatureFlagTypes.Rmt5VueBridgeDisabled), hint: 'ON = Vue bridge off' }] },
      { title: 'Migration flags', rows: RMT5_MIGRATION_FLAGS.map(toRow) },
      { title: 'Feature flags', rows: RMT5_FEATURE_FLAGS.map(toRow) },
    ];
    this.rmt5FlagRows = this.rmt5FlagSections.reduce((rows, section) => rows.concat(section.rows), []);
    this.homeFlagRows = HOME_DEBUG_FLAGS.map(toRow);
    const abOverride = UtilsProvider.featureFlagOverrides[FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest];
    if (abOverride === false) {
      this.abVariantSelection = AB_TEST_OFF;
    } else if (typeof abOverride === 'string' && LANDING_PAGE_AB_TEST_VARIANTS.indexOf(abOverride) >= 0) {
      this.abVariantSelection = abOverride;
    } else {
      this.abVariantSelection = '';
    }
  }

  serverFlagValue(flag: FeatureFlagTypes): boolean {
    const value = HttpProvider.featureFlags ? HttpProvider.featureFlags[flag] : undefined;
    if (value === undefined || value === null) {
      return false;
    }
    return value === true || value.toString().toLowerCase() === 'true';
  }

  private buildOverridesMap(): { [flag: string]: boolean | string } {
    const map: { [flag: string]: boolean | string } = {};
    this.rmt5FlagRows.concat(this.homeFlagRows).forEach((row) => {
      if (row.overridden) {
        map[row.flag] = row.value;
      }
    });
    if (this.abVariantSelection === AB_TEST_OFF) {
      map[FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest] = false;
    } else if (this.abVariantSelection) {
      map[FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest] = this.abVariantSelection;
    }
    // Only an "on" override is stored; off falls back to the server value.
    if (this.internalIssueReporter) {
      map[FeatureFlagTypes.InternalIssueReporterEnabled] = true;
    }
    return map;
  }

  private canonicalOverrides(map: { [flag: string]: boolean | string }): string {
    const out: { [flag: string]: boolean | string } = {};
    RMT5_DEBUG_FLAGS.concat(HOME_DEBUG_FLAGS).forEach((flag) => {
      if (map.hasOwnProperty(flag)) {
        out[flag] = map[flag];
      }
    });
    if (map.hasOwnProperty(FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest)) {
      out[FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest] = map[FeatureFlagTypes.Rmt5VueBridgeLandingPageAbTest];
    }
    if (map.hasOwnProperty(FeatureFlagTypes.InternalIssueReporterEnabled)) {
      out[FeatureFlagTypes.InternalIssueReporterEnabled] = map[FeatureFlagTypes.InternalIssueReporterEnabled];
    }
    return JSON.stringify(out);
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-debug-menu/rmt-debug-menu.ts